import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  UsersIcon,
  AcademicCapIcon,
  ClockIcon,
  StarIcon,
  MapPinIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";

/* ------------------------------------------------------------------ */
/* ROUTES — MANAGER SCOPE */
/* ------------------------------------------------------------------ */
const ROUTES = {
  assignTraining: "/manager/assign-trainings",
  calendar: "/manager/training-calender",
  courseCreation: "/manager/course-creation",
  workUpdates: "/manager/work-updates",
  appreciations: "/manager/appreciations",
  overtimeRules: "/manager/overtime-late-rules",
  liveTracking: "/manager/live-tracking-dashboard",
  // Appraisal Routes - Updated to new pages
  assignKPIs: "/manager/appraisal/assign",
  teamAppraisals: "/manager/appraisal/team",
  employeeProfile: (id) => `/manager/employee/${id}`,
};

/* ------------------------------------------------------------------ */
/* MOCK EMPLOYEE DATA & MASTER PROFILES */
/* ------------------------------------------------------------------ */
const EMPLOYEES = [
  { name: "Johnathan Alexander Doe", id: "EMP-2025-099", progress: "85%", appraisalStatus: "Goals Pending" },
  { name: "Arjun Mehta", id: "EMP101", progress: "40%", appraisalStatus: "Self-Review Done" },
  { name: "Priya Rai", id: "EMP104", progress: "70%", appraisalStatus: "Completed" },
];

const MASTER_DATA = {
  "EMP-2025-099": {
    code: "EMP-2025-099",
    firstName: "Johnathan",
    middleName: "Alexander",
    lastName: "Doe",
    type: "Permanent",
    doj: "2022-03-10",
    dob: "1994-06-22",
    gender: "Male",
    bloodGroup: "A+ Positive",
    profEmail: "john.doe@enterprise-ui.com",
    persEmail: "j.doe.dev@gmail.com",
    status: "Active",
    father: "Richard Doe",
    spouse: "Mary Doe",
    spouseDob: "1995-01-12",
    children: "Alice Doe (5), Ben Doe (2)",
    emergencyContact: "Mary Doe",
    emergencyRelation: "Spouse",
    emergencyMobile: "+91 9988776655",
    vehicle: "TS-07-JK-1234",
    dl: "DL-KA01-2022",
    pan: "ABCDE1234Z",
    aadhaar: "XXXX-XXXX-1122",
    nationality: "Indian",
    currentAddr: "123, Tech Park Residency, Hyderabad",
    permAddr: "45/A, Green Valley, Bangalore"
  }
};

const TeamManagement = () => {
  const [activeSubTab, setActiveSubTab] = useState("directory");
  const [empSearch, setEmpSearch] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const navigate = useNavigate();

  const tabs = [
    { id: "directory", label: "Team Directory", icon: UsersIcon },
    { id: "appraisals", label: "Appraisals", icon: ChartBarIcon }, // NEW TAB
    { id: "learning", label: "Learning & Growth", icon: AcademicCapIcon },
    { id: "productivity", label: "Work & Recognition", icon: StarIcon },
    { id: "schedules", label: "Shift Schedules", icon: ClockIcon },
  ];

  const filteredEmployees = EMPLOYEES.filter((emp) =>
    emp.id.toLowerCase().includes(empSearch.toLowerCase()) || 
    emp.name.toLowerCase().includes(empSearch.toLowerCase())
  );

  const getProfileData = (id) => MASTER_DATA[id] || { ...MASTER_DATA["EMP-2025-099"], code: id, firstName: id };

  return (
    <div className="min-h-screen bg-slate-50 font-roboto">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&family=Roboto:wght@400;500;700&display=swap');
        .font-poppins { font-family: 'Poppins', sans-serif; }
        .font-roboto { font-family: 'Roboto', sans-serif; }
      `}</style>

      {/* DETAILED PROFILE MODAL */}
      <AnimatePresence>
        {selectedProfile && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl rounded-xl">
              <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-xl font-700 font-poppins text-slate-900">Official Employee Profile</h2>
                  <p className="text-xs font-bold text-blue-600 uppercase tracking-widest">{selectedProfile.id}</p>
                </div>
                <button onClick={() => setSelectedProfile(null)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                  <XMarkIcon className="h-6 w-6 text-slate-400" />
                </button>
              </div>

              <div className="p-8">
                {/* Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-y-8 gap-x-12">
                  {[
                    { label: "Employee Code", val: getProfileData(selectedProfile.id).code },
                    { label: "First Name", val: getProfileData(selectedProfile.id).firstName },
                    { label: "Middle Name", val: getProfileData(selectedProfile.id).middleName },
                    { label: "Last Name", val: getProfileData(selectedProfile.id).lastName },
                    { label: "Employment Type", val: getProfileData(selectedProfile.id).type },
                    { label: "Date of Joining", val: getProfileData(selectedProfile.id).doj },
                    { label: "Date of Birth", val: getProfileData(selectedProfile.id).dob },
                    { label: "Gender", val: getProfileData(selectedProfile.id).gender },
                    { label: "Blood Group", val: getProfileData(selectedProfile.id).bloodGroup },
                    { label: "Professional Email", val: getProfileData(selectedProfile.id).profEmail },
                    { label: "Personal Email", val: getProfileData(selectedProfile.id).persEmail },
                    { label: "Employment Status", val: getProfileData(selectedProfile.id).status },
                    { label: "Father's Name", val: getProfileData(selectedProfile.id).father },
                    { label: "Spouse Name", val: getProfileData(selectedProfile.id).spouse },
                    { label: "Spouse DOB", val: getProfileData(selectedProfile.id).spouseDob },
                    { label: "Children Info", val: getProfileData(selectedProfile.id).children },
                    { label: "Emergency Contact", val: getProfileData(selectedProfile.id).emergencyContact },
                    { label: "Relation", val: getProfileData(selectedProfile.id).emergencyRelation },
                    { label: "Emergency Mobile", val: getProfileData(selectedProfile.id).emergencyMobile },
                    { label: "Vehicle Number", val: getProfileData(selectedProfile.id).vehicle },
                    { label: "Driving License", val: getProfileData(selectedProfile.id).dl },
                    { label: "PAN Number", val: getProfileData(selectedProfile.id).pan },
                    { label: "Aadhaar Number", val: getProfileData(selectedProfile.id).aadhaar },
                    { label: "Nationality", val: getProfileData(selectedProfile.id).nationality },
                  ].map((item, idx) => (
                    <div key={idx} className="border-b border-slate-100 pb-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">{item.label}</p>
                      <p className="text-sm font-500 text-slate-800">{item.val || "N/A"}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-12">
                   <div className="border-b border-slate-100 pb-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Current Residential Address</p>
                      <p className="text-sm text-slate-700 leading-relaxed">{getProfileData(selectedProfile.id).currentAddr}</p>
                   </div>
                   <div className="border-b border-slate-100 pb-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Permanent Address</p>
                      <p className="text-sm text-slate-700 leading-relaxed">{getProfileData(selectedProfile.id).permAddr}</p>
                   </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* SUB NAV */}
      <div className="flex gap-2 px-6 py-4 bg-white border-b border-slate-200 sticky top-0 z-40 overflow-x-auto no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-poppins font-bold uppercase whitespace-nowrap transition ${
              activeSubTab === tab.id
                ? "bg-blue-600 text-white shadow"
                : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        <AnimatePresence mode="wait">
          
          {/* ================= APPRAISALS TAB ================= */}
          {activeSubTab === "appraisals" && (
            <motion.div key="appraisals" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ToolCard 
                  title="Assign KPIs" 
                  desc="Define specific KPIs, weightage, and targets for your team members." 
                  onClick={() => navigate(ROUTES.assignKPIs)} 
                  badge="Manager Action"
                  icon={ClipboardDocumentCheckIcon}
                />
                <ToolCard 
                  title="Team Appraisals" 
                  desc="View team appraisal status and review employee submissions." 
                  onClick={() => navigate(ROUTES.teamAppraisals)} 
                  badge="Manager Action"
                  icon={UserCircleIcon}
                />
              </div>

              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                   <h3 className="text-xs font-bold font-poppins text-slate-500 uppercase">Quick Access</h3>
                </div>
                <div className="p-6">
                  <p className="text-sm text-slate-600 mb-4">Use the cards above to:</p>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span><strong>Assign KPIs:</strong> Set goals and weightage for team members</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span><strong>Team Appraisals:</strong> Review self-assessments and provide ratings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span><strong>Propose Compensation:</strong> Submit hike and bonus recommendations</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          )}

          {/* ================= TEAM DIRECTORY ================= */}
          {activeSubTab === "directory" && (
            <motion.div key="directory" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <ToolCard
                  title="Live Tracking"
                  desc="View real-time location & activity of field employees."
                  badge="Live"
                  icon={MapPinIcon}
                  onClick={() => navigate(ROUTES.liveTracking)}
                />
              </div>

              <div className="bg-white p-4 border border-slate-200 rounded-xl flex items-center justify-between">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Search Directory</label>
                  <input
                    type="text"
                    placeholder="Search Name or ID..."
                    value={empSearch}
                    onChange={(e) => setEmpSearch(e.target.value)}
                    className="w-full md:w-80 p-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <TeamTable
                employees={filteredEmployees}
                onViewProfile={(emp) => setSelectedProfile(emp)}
              />
            </motion.div>
          )}

          {/* ================= LEARNING & GROWTH ================= */}
          {activeSubTab === "learning" && (
            <motion.div key="learning" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ToolCard title="Assign Trainings" desc="Assign courses by employee or team." onClick={() => navigate(ROUTES.assignTraining)} badge="Manager Action" icon={AcademicCapIcon} />
              <ToolCard title="Training Calendar" desc="View training schedules." onClick={() => navigate(ROUTES.calendar)} badge="Manager Action" icon={ClockIcon} />
              <ToolCard title="Course Creation" desc="Create internal courses." onClick={() => navigate(ROUTES.courseCreation)} badge="Manager Action" icon={AcademicCapIcon} />
            </motion.div>
          )}

          {/* ================= WORK & RECOGNITION ================= */}
          {activeSubTab === "productivity" && (
            <motion.div key="productivity" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ToolCard title="Work Updates" desc="Track daily progress." onClick={() => navigate(ROUTES.workUpdates)} badge="Manager Action" icon={StarIcon} />
              <ToolCard title="Appreciations" desc="Recognize team efforts." onClick={() => navigate(ROUTES.appreciations)} badge="Manager Action" icon={StarIcon} />
            </motion.div>
          )}

          {/* ================= SHIFT SCHEDULES ================= */}
          {activeSubTab === "schedules" && (
            <motion.div key="schedules" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
              <div className="flex gap-3 bg-amber-50 border border-amber-200 p-4 rounded-xl">
                <InformationCircleIcon className="h-5 w-5 text-amber-600" />
                <p className="text-xs text-amber-700">Managers have full access to shift & overtime configuration.</p>
              </div>
              <ToolCard title="Overtime & Late Rules" desc="Configure policies." onClick={() => navigate(ROUTES.overtimeRules)} badge="Manager Action" icon={ClockIcon} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* COMPONENTS */
/* ------------------------------------------------------------------ */

const ToolCard = ({ title, desc, onClick, badge, icon: Icon }) => (
  <button onClick={onClick} className="text-left bg-white p-6 rounded-xl border border-slate-200 hover:border-blue-400 shadow-sm transition">
    <div className="flex justify-between mb-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-5 w-5 text-blue-500" />}
        <h4 className="text-sm font-poppins font-bold">{title}</h4>
      </div>
      <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase ${
        badge === "Manager Action" ? "bg-green-50 text-green-700" :
        badge === "Live" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"
      }`}>
        {badge}
      </span>
    </div>
    <p className="text-xs text-slate-500 mb-4">{desc}</p>
    <div className="flex items-center gap-1 text-[10px] font-bold text-blue-600 uppercase">
      Open Module <ArrowTopRightOnSquareIcon className="h-3 w-3" />
    </div>
  </button>
);

const TeamTable = ({ employees, onViewProfile }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
    <table className="w-full text-left">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          <th className="px-6 py-4 text-[10px] font-bold uppercase text-slate-400">Employee</th>
          <th className="px-6 py-4 text-[10px] font-bold uppercase text-slate-400">Employee ID</th>
          <th className="px-6 py-4 text-[10px] font-bold uppercase text-slate-400">Learning Progress</th>
          <th className="px-6 py-4 text-[10px] font-bold uppercase text-slate-400 text-right">Action</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {employees.length > 0 ? employees.map(emp => (
          <tr key={emp.id} className="hover:bg-slate-50">
            <td className="px-6 py-4 font-poppins font-medium text-slate-800">{emp.name}</td>
            <td className="px-6 py-4 text-xs font-bold text-blue-600">{emp.id}</td>
            <td className="px-6 py-4">
              <div className="w-24 h-1.5 bg-slate-200 rounded">
                <div className="h-full bg-blue-500" style={{ width: emp.progress }} />
              </div>
            </td>
            <td className="px-6 py-4 text-right">
              <button
                onClick={() => onViewProfile(emp)}
                className="text-[10px] font-bold text-blue-600 uppercase hover:underline"
              >
                View Profile
              </button>
            </td>
          </tr>
        )) : (
          <tr>
            <td colSpan="4" className="px-6 py-10 text-center text-sm text-slate-400 italic">No employee found</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export default TeamManagement;